import { useEffect, useRef } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type { OpenMainContentTabInput } from '../../store/layoutStoreTypes';
import { useCopilotFindingEditProcessingStore } from '../../store/copilotFindingEditProcessingStore';
import {
  FINDING_MAIN_CONTENT_TAB_TYPE,
  FINDING_TAB_MODE,
} from '../../store/layoutStoreTypes';
import {
  readCopilotFindingEditFieldCount,
  resolveCopilotFindingEditProcessingId,
} from './copilotObjectCreatorProgress';

type UseCopilotFindingEditProcessingProps = {
  chatId: string;
  isActive: boolean;
  messages: TerraUIMessage[];
  isLoadingHistory: boolean;
  linkedObject?: {
    kind: string;
    id: string;
  };
  navigateInActiveTab: (tab: OpenMainContentTabInput) => void;
  revealMainContentIfDetachedCopilot: () => void;
  findingDetailsTabTitle: string;
};

export function useCopilotFindingEditProcessing({
  chatId,
  isActive,
  messages,
  isLoadingHistory,
  linkedObject,
  navigateInActiveTab,
  revealMainContentIfDetachedCopilot,
  findingDetailsTabTitle,
}: UseCopilotFindingEditProcessingProps): void {
  const navigatedProcessingFindingIdRef = useRef<string | null>(null);
  const stopSuppressedUserMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoadingHistory) {
      return;
    }

    const processingStore = useCopilotFindingEditProcessingStore.getState();
    const lastUserMessageId =
      messages.findLast((message) => message.role === 'user')?.id ?? null;

    if (processingStore.stopSuppressionChatId === chatId) {
      if (stopSuppressedUserMessageIdRef.current === null) {
        stopSuppressedUserMessageIdRef.current = lastUserMessageId;
      }

      if (lastUserMessageId === stopSuppressedUserMessageIdRef.current) {
        navigatedProcessingFindingIdRef.current = null;

        if (
          processingStore.sourceChatId === chatId &&
          processingStore.processingFindingId
        ) {
          processingStore.releaseProcessingFindingEditForChat(chatId);
        }

        return;
      }

      processingStore.clearStopSuppression();
      stopSuppressedUserMessageIdRef.current = null;
    } else {
      stopSuppressedUserMessageIdRef.current = null;
    }

    const linkedFindingId =
      linkedObject?.kind === 'Finding' ? linkedObject.id : undefined;
    const detectedProcessingFindingId = resolveCopilotFindingEditProcessingId({
      messages,
      linkedFindingId,
    });
    const currentProcessing = useCopilotFindingEditProcessingStore.getState();

    if (detectedProcessingFindingId) {
      const editingFieldCount = readCopilotFindingEditFieldCount({
        messages,
        findingId: detectedProcessingFindingId,
      });

      useCopilotFindingEditProcessingStore.getState().setProcessingFindingEdit({
        findingId: detectedProcessingFindingId,
        chatId,
        editingFieldCount,
      });

      if (!isActive) {
        return;
      }

      if (
        navigatedProcessingFindingIdRef.current === detectedProcessingFindingId
      ) {
        return;
      }

      navigatedProcessingFindingIdRef.current = detectedProcessingFindingId;
      revealMainContentIfDetachedCopilot();
      navigateInActiveTab({
        id: `finding-edit-${detectedProcessingFindingId}`,
        title: findingDetailsTabTitle,
        type: FINDING_MAIN_CONTENT_TAB_TYPE,
        data: {
          findingId: detectedProcessingFindingId,
          mode: FINDING_TAB_MODE.EDIT,
        },
      });

      return;
    }

    if (
      currentProcessing.sourceChatId === chatId &&
      currentProcessing.processingFindingId
    ) {
      navigatedProcessingFindingIdRef.current = null;
      useCopilotFindingEditProcessingStore
        .getState()
        .releaseProcessingFindingEditForChat(chatId);
    }
  }, [
    chatId,
    findingDetailsTabTitle,
    isActive,
    isLoadingHistory,
    linkedObject?.id,
    linkedObject?.kind,
    messages,
    navigateInActiveTab,
    revealMainContentIfDetachedCopilot,
  ]);
}
